import { useStore } from "ra-core";
import { useCallback, useEffect } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

export type EntityFieldType =
  | "text"
  | "textarea"
  | "select"
  | "number"
  | "currency"
  | "date"
  | "email"
  | "tel";

export const ENTITY_FIELD_TYPE_LABELS: Record<EntityFieldType, string> = {
  text: "Texto (linha única)",
  textarea: "Texto longo",
  select: "Opção única",
  number: "Número",
  currency: "Monetário (R$)",
  date: "Data",
  email: "E-mail",
  tel: "Telefone",
};

/**
 * Visibility / display settings for an entity custom field.
 *
 * showOnDealCreate   – rendered inside the "New Deal" form (DealInputs)
 * showOnEntityCreate – rendered inside QuickCreate dialogs (Contact / Company)
 * showOnDealPanel    – rendered inside the Deal Show sidebar section
 */
export interface EntityFieldVisibility {
  showOnDealCreate: boolean;
  showOnEntityCreate: boolean;
  showOnDealPanel: boolean;
}

export interface EntityCustomField {
  id: string;
  name: string;
  type: EntityFieldType;
  options?: string[]; // for "select"
  visibility: EntityFieldVisibility;
  /** Whether the field is required inside the context where it is shown */
  required?: boolean;
  order: number;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_CONTACT_FIELDS: EntityCustomField[] = [
  {
    id: "sys_first_name",
    name: "Primeiro Nome",
    type: "text",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    required: true,
    order: 0,
  },
  {
    id: "sys_last_name",
    name: "Sobrenome",
    type: "text",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 1,
  },
  {
    id: "sys_title",
    name: "Cargo / Função",
    type: "text",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 2,
  },
  {
    id: "sys_email",
    name: "E-mail",
    type: "email",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 3,
  },
  {
    id: "sys_phone",
    name: "Telefone / WhatsApp",
    type: "tel",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 4,
  },
  {
    id: "sys_linkedin",
    name: "LinkedIn (URL)",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 5,
  },
  {
    id: "sys_background",
    name: "Observações / Contexto",
    type: "textarea",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 6,
  },
];

const DEFAULT_COMPANY_FIELDS: EntityCustomField[] = [
  {
    id: "sys_name",
    name: "Nome / Razão Social",
    type: "text",
    visibility: {
      showOnDealCreate: true,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    required: true,
    order: 0,
  },
  {
    id: "sys_nome_fantasia",
    name: "Nome Fantasia",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 1,
  },
  {
    id: "sys_tax_identifier",
    name: "CNPJ / CPF",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 2,
  },
  {
    id: "sys_sector",
    name: "Setor de Atuação",
    type: "select",
    options: [
      "Serviços",
      "Tecnologia",
      "Comércio",
      "Indústria",
      "Saúde",
      "Educação",
      "Financeiro",
      "Construção",
      "Agronegócio",
      "Outros",
    ],
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 3,
  },
  {
    id: "sys_regime_tributario",
    name: "Regime Tributário",
    type: "select",
    options: ["Simples Nacional", "Lucro Presumido", "Lucro Real", "MEI"],
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 4,
  },
  {
    id: "sys_faturamento_mensal",
    name: "Faturamento Mensal (R$)",
    type: "currency",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 5,
  },
  {
    id: "sys_numero_funcionarios",
    name: "Nº de Funcionários",
    type: "number",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 6,
  },
  {
    id: "sys_atividade_principal",
    name: "Atividade Principal",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 7,
  },
  {
    id: "sys_socio_1",
    name: "Sócio 1 (Administrador)",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 8,
  },
  {
    id: "sys_socio_2",
    name: "Sócio 2",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: false,
      showOnDealPanel: true,
    },
    order: 9,
  },
  {
    id: "sys_phone_number",
    name: "Telefone da Empresa",
    type: "tel",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 10,
  },
  {
    id: "sys_website",
    name: "Website",
    type: "text",
    visibility: {
      showOnDealCreate: false,
      showOnEntityCreate: true,
      showOnDealPanel: true,
    },
    order: 11,
  },
];

// ─── Store Keys ───────────────────────────────────────────────────────────────

export const DEFAULT_CRM_ID = "fe386f56-b27f-446f-86ec-39a03b64add1"; // Grupo Dotus

export const getActiveCrmId = (): string => {
  if (typeof window === "undefined" || !window.localStorage) {
    return DEFAULT_CRM_ID;
  }
  const raw = window.localStorage.getItem("dotus_active_crm_id");
  if (!raw || raw === "00000000-0000-0000-0000-000000000001") {
    return DEFAULT_CRM_ID;
  }
  return raw;
};

export const getEntityFieldsStoreKey = (
  entity: EntityType,
  crmId: string = getActiveCrmId(),
) => `crm.${crmId}.${entity}_entity_fields`;

// ─── Hook ─────────────────────────────────────────────────────────────────────

type EntityType = "contact" | "company";

const DEFAULTS: Record<EntityType, EntityCustomField[]> = {
  contact: DEFAULT_CONTACT_FIELDS,
  company: DEFAULT_COMPANY_FIELDS,
};

async function syncEntityFieldsToSupabase(
  key: "contactCustomFields" | "companyCustomFields",
  fields: EntityCustomField[],
  targetCrmId: string = getActiveCrmId(),
) {
  try {
    const client = getSupabaseClient();
    const activeCrmId = targetCrmId || getActiveCrmId();

    // 1. Sincroniza nas configurações do CRM específico
    if (activeCrmId) {
      try {
        const { data: crmData } = await client
          .from("crms")
          .select("settings")
          .eq("id", activeCrmId)
          .maybeSingle();
        const settings = crmData?.settings || {};
        await client
          .from("crms")
          .update({ settings: { ...settings, [key]: fields } })
          .eq("id", activeCrmId);
      } catch (err) {
        console.warn(
          "Could not sync entity custom fields to crms settings:",
          err,
        );
      }
    }

    // 2. Sincroniza também na tabela formal custom_field_definitions escopado por crm_id
    const entityType = key === "contactCustomFields" ? "contact" : "company";
    for (const f of fields) {
      if (!f.id || !f.name) continue;
      await client.from("custom_field_definitions").upsert(
        {
          crm_id: activeCrmId,
          entity_type: entityType,
          key: f.id,
          label: f.name,
          field_type: f.type || "text",
          options: f.options || [],
          order_nr: f.order ?? 0,
          required: !!f.required,
        },
        { onConflict: "crm_id,entity_type,key" },
      );
    }
  } catch (err) {
    console.warn("Could not sync custom fields to Supabase:", err);
  }
}

export function useEntityCustomFields(entity: EntityType) {
  const activeCrmId = getActiveCrmId();
  const configKey =
    entity === "contact" ? "contactCustomFields" : "companyCustomFields";

  const storeKey = getEntityFieldsStoreKey(entity, activeCrmId);

  const [fields, setFields] = useStore<EntityCustomField[]>(
    storeKey,
    DEFAULTS[entity],
  );

  // Sync from Supabase on mount or whenever activeCrmId changes
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const client = getSupabaseClient();
        // 1. Carrega da tabela custom_field_definitions do crm ativo
        const { data: dbDefinitions } = await client
          .from("custom_field_definitions")
          .select("*")
          .eq("crm_id", activeCrmId)
          .eq("entity_type", entity)
          .order("order_nr", { ascending: true });

        if (isMounted && dbDefinitions && dbDefinitions.length > 0) {
          const mapped: EntityCustomField[] = dbDefinitions.map((d: any) => ({
            id: d.key,
            name: d.label,
            type: d.field_type as EntityFieldType,
            options: Array.isArray(d.options) ? d.options : [],
            order: d.order_nr ?? 0,
            required: !!d.required,
            visibility: {
              showOnDealCreate: true,
              showOnEntityCreate: true,
              showOnDealPanel: true,
            },
          }));
          setFields(mapped);
          return;
        }

        // 2. Fallback: carrega das settings do CRM específico
        const { data: crmData } = await client
          .from("crms")
          .select("settings")
          .eq("id", activeCrmId)
          .maybeSingle();

        const saved = crmData?.settings?.[configKey];
        if (isMounted && saved && Array.isArray(saved) && saved.length > 0) {
          setFields(saved);
          return;
        }

        if (isMounted) {
          setFields(DEFAULTS[entity]);
        }
      } catch {}
    })();
    return () => {
      isMounted = false;
    };
  }, [entity, configKey, activeCrmId, setFields]);

  const sorted = [...(fields || DEFAULTS[entity])].sort(
    (a, b) => a.order - b.order,
  );

  /** All fields regardless of visibility */
  const allFields = sorted;

  /** Fields shown at deal creation */
  const dealCreateFields = sorted.filter((f) => f.visibility.showOnDealCreate);

  /** Fields shown inside QuickCreate dialogs */
  const entityCreateFields = sorted.filter(
    (f) => f.visibility.showOnEntityCreate,
  );

  /** Fields shown in the deal panel sidebar */
  const dealPanelFields = sorted.filter((f) => f.visibility.showOnDealPanel);

  // ─── CRUD ─────────────────────────────────────────────────────────────────

  const addField = useCallback(
    (data: Omit<EntityCustomField, "id" | "order">) => {
      let slug =
        "cf_" +
        data.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]/g, "_")
          .replace(/_+/g, "_")
          .substring(0, 30);
      if (slug.endsWith("_")) slug = slug.slice(0, -1);

      const newField: EntityCustomField = {
        ...data,
        id: slug,
        order: (fields || []).length,
      };

      const updated = [...(fields || []), newField];
      setFields(updated);
      syncEntityFieldsToSupabase(configKey, updated, activeCrmId);
      return newField;
    },
    [configKey, fields, setFields, activeCrmId],
  );

  const updateField = useCallback(
    (id: string, patch: Partial<Omit<EntityCustomField, "id">>) => {
      const updated = (fields || []).map((f) =>
        f.id === id ? { ...f, ...patch } : f,
      );
      setFields(updated);
      syncEntityFieldsToSupabase(configKey, updated, activeCrmId);
    },
    [configKey, fields, setFields, activeCrmId],
  );

  const deleteField = useCallback(
    async (id: string) => {
      const updated = (fields || []).filter((f) => f.id !== id);
      setFields(updated);
      syncEntityFieldsToSupabase(configKey, updated, activeCrmId);
      try {
        await getSupabaseClient()
          .from("custom_field_definitions")
          .delete()
          .eq("crm_id", activeCrmId)
          .eq("entity_type", entity)
          .eq("key", id);
      } catch (err) {
        console.warn("Could not delete from custom_field_definitions:", err);
      }
    },
    [configKey, fields, setFields, activeCrmId, entity],
  );

  const reorderFields = useCallback(
    (reordered: EntityCustomField[]) => {
      const updated = reordered.map((f, idx) => ({ ...f, order: idx }));
      setFields(updated);
      syncEntityFieldsToSupabase(configKey, updated, activeCrmId);
    },
    [configKey, setFields, activeCrmId],
  );

  return {
    allFields,
    dealCreateFields,
    entityCreateFields,
    dealPanelFields,
    addField,
    updateField,
    deleteField,
    reorderFields,
  };
}
